import React, { useState, useContext } from 'react';
import { Download, Upload, Database, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { AuthContext } from '../App';

const DataManagement = () => {
  const { user } = useContext(AuthContext);
  const [selectedTypes, setSelectedTypes] = useState({
    services: true,
    packages: true,
    quotes: true,
    customers: true,
    companies: false,
    revenue: true
  });
  const [exportFormat, setExportFormat] = useState('json');
  const [importFile, setImportFile] = useState(null);
  const [conflictStrategy, setConflictStrategy] = useState('ask');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const companyId = user?.tenant_id ? `company_${user.tenant_id}` : 'default_company';

  const dataTypeLabels = {
    services: 'Services',
    packages: 'Service-Pakete',
    quotes: 'Angebote',
    customers: 'Kunden',
    companies: 'Firmendaten',
    revenue: 'Umsatzdaten'
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      setMessage(null);

      const selectedTypesList = Object.keys(selectedTypes).filter(key => selectedTypes[key]);
      
      if (selectedTypesList.length === 0) {
        setMessage({ type: 'error', text: 'Bitte wählen Sie mindestens einen Datentyp aus' });
        return;
      }

      const params = new URLSearchParams();
      params.append('company_id', companyId);
      selectedTypesList.forEach(type => params.append('data_types', type));
      params.append('format', exportFormat);

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/data-management/export?${params}`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Export fehlgeschlagen');
      }

      // Get filename from headers
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?(.+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `export_${Date.now()}.${exportFormat}`;

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setMessage({ type: 'success', text: `Export erfolgreich: ${filename}` });
    } catch (error) {
      console.error('Export error:', error);
      setMessage({ type: 'error', text: `Export fehlgeschlagen: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    try {
      setLoading(true);
      setMessage(null);

      if (!importFile) {
        setMessage({ type: 'error', text: 'Bitte wählen Sie eine Datei aus' });
        return;
      }

      const formData = new FormData();
      formData.append('file', importFile);

      const params = new URLSearchParams();
      params.append('company_id', companyId);
      params.append('conflict_strategy', conflictStrategy);

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/data-management/import?${params}`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Import fehlgeschlagen');
      }

      const result = await response.json();
      
      const importedTotal = Object.values(result.results.imported).reduce((a, b) => a + b, 0);
      const skippedTotal = Object.values(result.results.skipped).reduce((a, b) => a + b, 0);
      
      let messageText = `Import erfolgreich: ${importedTotal} Einträge importiert`;
      if (skippedTotal > 0) {
        messageText += `, ${skippedTotal} übersprungen`;
      }
      if (result.results.errors.length > 0) {
        messageText += `. ${result.results.errors.length} Fehler aufgetreten.`;
      }

      setMessage({ type: 'success', text: messageText });
      setImportFile(null);
    } catch (error) {
      console.error('Import error:', error);
      setMessage({ type: 'error', text: `Import fehlgeschlagen: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ['.json', '.xlsx', '.csv'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (validExtensions.includes(fileExtension)) {
        setImportFile(file);
        setMessage(null);
      } else {
        setMessage({ type: 'error', text: 'Ungültiges Dateiformat. Bitte wählen Sie JSON, Excel oder CSV.' });
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <Database className="w-8 h-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-800">Daten Export / Import</h1>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <span>{message.text}</span>
          </div>
        )}

        {/* Export Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Download className="w-5 h-5" />
            Daten exportieren
          </h2>

          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h3 className="font-medium text-gray-700 mb-3">Datentypen auswählen:</h3>
            <div className="grid grid-cols-2 gap-3">
              {Object.keys(dataTypeLabels).map(key => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedTypes[key]}
                    onChange={(e) => setSelectedTypes(prev => ({...prev, [key]: e.target.checked}))}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-700">{dataTypeLabels[key]}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h3 className="font-medium text-gray-700 mb-3">Export-Format:</h3>
            <div className="flex gap-4">
              {['json', 'excel', 'csv'].map(format => (
                <label key={format} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="exportFormat"
                    value={format}
                    checked={exportFormat === format}
                    onChange={(e) => setExportFormat(e.target.value)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700 uppercase">{format}</span>
                </label>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {exportFormat === 'json' && 'JSON: Vollständiger Export mit allen Details'}
              {exportFormat === 'excel' && 'Excel: Übersichtlich und editierbar'}
              {exportFormat === 'csv' && 'CSV: Einfaches Format (nur erster Datentyp)'}
            </p>
          </div>

          <button
            onClick={handleExport}
            disabled={loading}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Exportiere...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Jetzt exportieren
              </>
            )}
          </button>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 my-8"></div>

        {/* Import Section */}
        <div>
          <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Daten importieren
          </h2>

          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h3 className="font-medium text-gray-700 mb-3">Datei auswählen:</h3>
            <input
              type="file"
              accept=".json,.xlsx,.csv"
              onChange={handleFileChange}
              className="w-full text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {importFile && (
              <p className="text-sm text-gray-600 mt-2">
                Ausgewählt: {importFile.name} ({(importFile.size / 1024).toFixed(2)} KB)
              </p>
            )}
          </div>

          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h3 className="font-medium text-gray-700 mb-3">Bei Konflikten:</h3>
            <div className="space-y-2">
              {[
                { value: 'skip', label: 'Überspringen', desc: 'Bestehende Einträge behalten' },
                { value: 'overwrite', label: 'Überschreiben', desc: 'Bestehende Einträge ersetzen' },
                { value: 'ask', label: 'Fragen', desc: 'Für jeden Konflikt nachfragen (empfohlen)' }
              ].map(option => (
                <label key={option.value} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="conflictStrategy"
                    value={option.value}
                    checked={conflictStrategy === option.value}
                    onChange={(e) => setConflictStrategy(e.target.value)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 mt-0.5"
                  />
                  <div>
                    <div className="text-gray-700 font-medium">{option.label}</div>
                    <div className="text-sm text-gray-500">{option.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <button
            onClick={handleImport}
            disabled={loading || !importFile}
            className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Importiere...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Jetzt importieren
              </>
            )}
          </button>
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">💡 Hinweise:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Exportieren Sie regelmäßig Ihre Daten zur Sicherung</li>
            <li>• JSON-Format enthält alle Details und ist am vollständigsten</li>
            <li>• Excel-Format ist ideal zum Bearbeiten und Überprüfen</li>
            <li>• Beim Import werden Ihre Daten mit den importierten Daten zusammengeführt</li>
            <li>• Automatische Backups werden täglich erstellt und gespeichert</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DataManagement;
